// Carry Roll DispersionMap — 2D dispersion of shots (7.6/7.8).

import { useStore } from '../../store';
import { convertDistance, type DistanceUnit } from '@carry-roll/shared-logic';
import { simulate } from '@carry-roll/shared-logic';

const SVG_W = 300;
const SVG_H = 200;
const PAD_L = 35;
const PAD_R = 15;
const PAD_T = 15;
const PAD_B = 25;
const PLOT_W = SVG_W - PAD_L - PAD_R;
const PLOT_H = SVG_H - PAD_T - PAD_B;
const CY = PAD_T + PLOT_H / 2;

// Fixed scale ranges
const MAX_CARRY_M = 300;
const MAX_OFFLINE_M = 30;

export function DispersionMap() {
  const scenarios = useStore((s) => s.scenarios);
  const distanceUnit = useStore((s) => s.distanceUnit);

  if (scenarios.length === 0) {
    return (
      <div className="panel" data-testid="dispersion-map" style={{ color: 'var(--color-text-muted)' }}>
        No shots loaded. Add scenarios with &quot;Add current shot&quot; from the Compare panel.
      </div>
    );
  }

  const points = scenarios.map((s) => {
    const result = simulate(s.request);
    return {
      id: s.id,
      label: s.label,
      carryM: result.carryM,
      offlineM: result.offlineM,
    };
  });

  const conv = (m: number) => convertDistance(m, distanceUnit);
  const u: DistanceUnit = distanceUnit;

  // Scale helpers
  const sx = (carryM: number) => PAD_L + Math.min(carryM / MAX_CARRY_M, 1) * PLOT_W;
  const sy = (offlineM: number) => CY - Math.max(-1, Math.min(1, offlineM / MAX_OFFLINE_M)) * (PLOT_H / 2);

  return (
    <div className="panel" data-testid="dispersion-map">
      <h3 className="subsection-title">
        Dispersion map
      </h3>
      <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
        Each point is a scenario&apos;s finish: horizontal axis is carry, vertical axis is offline from centre.
      </p>
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} width="100%" role="img" aria-label="Shot dispersion — carry vs offline">
        {/* Axes */}
        <line x1={PAD_L} y1={CY} x2={SVG_W - PAD_R} y2={CY} stroke="var(--color-border)" strokeWidth={1} />
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={SVG_H - PAD_B} stroke="var(--color-border)" strokeWidth={1} />

        {/* Target line (center) */}
        <line x1={PAD_L} y1={CY} x2={SVG_W - PAD_R} y2={CY} stroke="var(--color-text-muted)" strokeWidth={0.5} strokeDasharray="4 4" />

        {/* Axis labels */}
        <text x={PAD_L + PLOT_W / 2} y={SVG_H - 5} textAnchor="middle" fontSize="10" fill="var(--color-text-muted)">
          Carry ({u})
        </text>
        <text x={8} y={CY} textAnchor="middle" fontSize="10" fill="var(--color-text-muted)" transform={`rotate(-90 8 ${CY})`}>
          Offline ({u})
        </text>

        {/* Tick marks */}
        {[0, 100, 200, 300].map((m) => (
          <g key={m}>
            <line x1={sx(m)} y1={SVG_H - PAD_B} x2={sx(m)} y2={SVG_H - PAD_B + 3} stroke="var(--color-border)" />
            <text x={sx(m)} y={SVG_H - PAD_B + 12} textAnchor="middle" fontSize="8" fill="var(--color-text-muted)">
              {Math.round(conv(m))}
            </text>
          </g>
        ))}

        {/* Dispersion points */}
        {points.map((p) => (
          <circle
            key={p.id}
            cx={sx(p.carryM)}
            cy={sy(p.offlineM)}
            r={5}
            fill="var(--color-carry)"
            opacity={0.7}
            aria-label={`${p.label}: ${Math.round(conv(p.carryM))} ${u} carry, ${Math.round(conv(p.offlineM))} ${u} offline`}
          >
            <title>{`${p.label}: ${Math.round(conv(p.carryM))} ${u} carry, ${Math.round(conv(p.offlineM))} ${u} offline`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
}
